import * as React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Helpers, Metrics } from '@origin-digital/style-guide';
import { Box } from '@origin-digital/layout';
import Grid from '../../components/MuiGrid/Grid';
import { SolarBillPeriod, SolarRecommendationOptions } from '../../../__generated__/globalTypes';
import analytics from '../../analytics';
import { GreyBackground, ResponsiveLayout } from '../../components/ResponsiveLayout';
import { Banner } from '../../components';
import { FormErrorSummary } from '../../components/Form/Layout';
import { CALCULATE_SOLAR_SAVINGS } from '../../graphql/queries';
import { CalculatorForm, CalculatorResult } from './components';
import { CalculatorFormValues } from './components/CalculatorForm/CalculatorForm.types';
import { SolarCalculatorState, SolarCalculatorProps } from './SolarCalculator.types';
import { CalculatorDisclaimer } from './components/CalculatorDisclaimer/';
import logger from '../../logging';
import { calculateSolarSavings } from '../../redux/thunks';
import { FormikValues } from 'formik';
import { getQueryParameters } from '../../utils/url/urlHelper';
import { createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles';
import { WhyOrigin } from '../../components/WhyOrigin';
import { WHY_ORIGIN_SOLAR_ITEMS } from './resource/WhyOriginSolarContent';
import { CHAT_WITH_US_CONTENT } from '../../components/ChatWithUs/ChatWithUsSolarContent';
import ChatWithUs from '../../components/ChatWithUs/ChatWithUs';

const Content = styled.div`
  width: 100%;
  padding-top: ${Metrics.Spacing.P_Y_XXL}px;
  padding-bottom: ${Metrics.Spacing.P_Y_XXL}px;

  ${Helpers.media.LG`
    display: flex;
    justify-content: center;
    align-items: start;
  `};
`;

const theme = createMuiTheme({
  overrides: {
    MuiTooltip: {
      tooltip: {
        fontSize: '15px'
      }
    }
  }
});

export class SolarCalculator extends React.Component<SolarCalculatorProps, SolarCalculatorState> {
  public state: SolarCalculatorState = {
    activeTabSize: '',
    calculationCount: 0,
    fromPackageSection: false,
    loaded: false,
    values: false
  };

  private resultRef: React.RefObject<CalculatorResult>;
  private disclaimerRef: React.RefObject<CalculatorDisclaimer>;

  public componentDidMount() {
    this.resultRef = React.createRef();
    this.disclaimerRef = React.createRef();
    analytics.pageLoad('solar-estimate-savings');
  }

  public render(): JSX.Element {
    const { loaded, values, calculationCount, fromPackageSection, activeTabSize } = this.state;
    const { asyncSubmitting, recommendations } = this.props;

    return (
      <MuiThemeProvider theme={theme}>
        <GreyBackground data-id="solar-calculator">
          <Banner
            title={'What savings could you make with solar?'}
            subtitle={
              // tslint:disable-next-line
              'We can estimate your purchase cost, your electricity bill savings, and how long till your system is paying for itself.'
            }
          />
          <ResponsiveLayout min={['LG', 'XL']}>
            <FormErrorSummary marginTop={6} />
            <Content>
              <Grid container direction="row" justify="center" className="solar-calc-grid-ie">
                <Grid
                  item
                  xs={11}
                  sm={10}
                  md={loaded ? 5 : 6}
                  lg={loaded ? 5 : 6}
                  xl={loaded ? 5 : 6}
                >
                  <CalculatorForm loading={asyncSubmitting} onSubmit={this.submit} />
                </Grid>
                {loaded && (
                  <Grid item xs={11} sm={10} md={7} lg={7} xl={7}>
                    <CalculatorResult
                      fromPackageSection={fromPackageSection}
                      activeTabSize={activeTabSize}
                      loading={asyncSubmitting}
                      loaded={loaded}
                      updateActiveTabSize={this.updateActiveTabSize}
                      updateFromPackageCall={this.updateFromPackageCall}
                      recommendations={recommendations}
                      values={values}
                      onShowDisclaimer={this.onShowDisclaimer}
                      calculationCount={calculationCount}
                      ref={this.resultRef}
                      submit={this.submit}
                      forDate={this.props.forDate}
                    />
                  </Grid>
                )}
              </Grid>
            </Content>
          </ResponsiveLayout>
          <Box display="flex" paddingY={12} justifyContent="center" color="white" marginBottom={4}>
            <Grid
              container
              direction="row"
              justify="center"
              className="why-origin-grid-container-ie"
            >
              <Grid item xs={11} sm={10} md={9} lg={8} xl={8} className="why-origin-grid-ie">
                <WhyOrigin
                  headline="Why choose Origin Solar"
                  items={WHY_ORIGIN_SOLAR_ITEMS}
                  color="white"
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            display="flex"
            paddingY={12}
            justifyContent="center"
            color="white"
            marginBottom={4}
            className="chat-with-us-container-ie"
          >
            <Grid container direction="row" justify="center">
              <Grid item xs={11} sm={10} md={9} lg={8} xl={8}>
                <ChatWithUs
                  icon={CHAT_WITH_US_CONTENT.iconSvg}
                  headline={CHAT_WITH_US_CONTENT.headline}
                  description={CHAT_WITH_US_CONTENT.description}
                  buttonText={CHAT_WITH_US_CONTENT.buttonText}
                  buttonLink={CHAT_WITH_US_CONTENT.buttonLink}
                />
              </Grid>
            </Grid>
          </Box>
          <CalculatorDisclaimer ref={this.disclaimerRef} />
        </GreyBackground>
      </MuiThemeProvider>
    );
  }

  public updateActiveTabSize = (tabSize: string) => {
    this.setState({ activeTabSize: tabSize });
  };

  public updateFromPackageCall = (fromPackageSection: boolean) => {
    this.setState({ fromPackageSection });
  };

  public submit = async (values: CalculatorFormValues, from?: string) => {
    if (from !== 'packageCall') {
      this.setState({ values, fromPackageSection: false, activeTabSize: '' });
    } else {
      this.setState({ values, fromPackageSection: true });
    }

    try {
      await this.props.calculateSolarSavings(CALCULATE_SOLAR_SAVINGS, this.getValues(values));

      // This was a quick fix just for analytics to differentiate what things were clicking where
      if (from !== 'packageCall') {
        analytics.submitSuccessful({
          currentAction: {
            postcode: values.postcode,
            quarterlyBillSize: values.averageBill,
            typicalEnergyUsageTime: values.usagePercentDuringDay
          }
        });
      }

      this.setState({
        calculationCount: this.state.calculationCount + 1,
        loaded: true
      });
    } catch (error) {
      logger.error('GraphQL Error', error);
      return;
    }
  };

  private getValues = (values: FormikValues): SolarRecommendationOptions => {
    return {
      address: { postcode: values.postcode },
      billCostForPeriod: parseFloat(values.averageBill),
      billPeriod: SolarBillPeriod.QUARTERLY,
      forDate: this.props.forDate,
      package: values.package || 'advantage',
      usagePercentDuringDay: parseFloat(values.usagePercentDuringDay)
    };
  };

  private onShowDisclaimer = () => {
    const disclaimerRef = this.disclaimerRef.current;
    if (disclaimerRef) {
      disclaimerRef.scrollTo();
    }
  };
}

const mapStateToProps = (state: any) => {
  const queryParameters = getQueryParameters();
  return {
    asyncSubmitting: state.request.submitting,
    forDate: queryParameters.forDate,
    recommendations: {
      estimatedSystemSizeWatts: state.request.estimatedSystemSizeWatts,
      primaryRecommendationId: state.request.primaryRecommendationId,
      recommendations: state.request.recommendations
    }
  };
};

export default connect(
  mapStateToProps,
  { calculateSolarSavings }
)(SolarCalculator);
